// <copyright file="common-service.ts" company="BD">
// Copyright (c) BD. All rights reserved.
// Created: 12/26/2025 by Pankaj Mishra
// </copyright>

import { computed, inject, Injectable, signal } from "@angular/core";
import { MobileSoftwareUpgradeApiService } from "../../core/http/apiservice";
import { RegionApiItem, RegionApiResponse, WebAppInfo } from "../model/app-data.model";
import { mobileSoftwareUpgradeEndPoint } from "../../../assets/data/softwareupgradeconstant";
import { Observable, tap } from "rxjs";
import { NgxPermissionsService } from "ngx-permissions";
import { UserProfile } from "../model/user.model";
import { userProfileEndPoint, webAppVersion } from "../../../assets/data/apiconstant";

@Injectable({ providedIn: "root" })
// Defines CommonAPiService.
export class CommonAPiService {
  private readonly apiService = inject(MobileSoftwareUpgradeApiService);
  private ngxPermissions = inject(NgxPermissionsService);

  roleList = signal<string[]>([]);
  regionCountries = signal<RegionApiItem[]>([]);
  userSignal = signal<UserProfile | null | null>(null);
  private webAppVersionSignal = signal<WebAppInfo | null>(null);

  readonly roles = this.roleList.asReadonly();
  readonly region = this.regionCountries.asReadonly();
  readonly users = this.userSignal.asReadonly();
  readonly webAppVersion = this.webAppVersionSignal.asReadonly();

  // Derive user roles from profile - reactively updates when user changes
  roleSignal = computed<string[]>(() => {
    const user = this.userSignal();
    return user ? [user.roles] : [];
  });

  // Derive permissions from profile - reactively updates when user changes
  permissionsSignal = computed<string[]>(() => {
    const user = this.userSignal();
    return user ? user.permissions : [];
  });

  getRegionAPiResponse(): Observable<RegionApiResponse> {
    return this.apiService
      .get<RegionApiResponse>(
        mobileSoftwareUpgradeEndPoint.softwareUpgradeComponent.getRegionAndCountry
      )
      .pipe(
        // Update signals and load permissions into ngx-permissions
        tap((regionCountry) => {
          this.regionCountries.set(regionCountry.regions);
        })
      );
  }

  // Fetch user profile and load permissions into the app
  fetchProfile() {
    return this.apiService.get<UserProfile>(userProfileEndPoint.userProfileAndPermission).pipe(
      // Update signals and load permissions into ngx-permissions
      tap((user) => {
        this.userSignal.set(user);
        this.ngxPermissions.loadPermissions(this.permissionsSignal());
      })
    );
  }
  fetchRoleList() {
    return this.apiService.get<string[]>(userProfileEndPoint.roleList).pipe(
      tap((roles) => {
        this.roleList.set(roles);
      })
    );
  }
  fetchWebAppVersion(): Observable<WebAppInfo> {
    return this.apiService.get<WebAppInfo>(webAppVersion).pipe(
      tap((response) => {
        this.webAppVersionSignal.set(response);
      })
    );
  }
}
